/**
 * @file Toaster.tsx
 * @description Fixed-position toast list rendered at the app root. Reads
 *   the toast store, schedules auto-dismiss per item via setTimeout, and
 *   renders each toast with a kind-appropriate colour and an accessible
 *   role="alert" so screen readers announce it.
 * @module Components/Common
 * @security Renders only `toast.message` (an already-translated string).
 *   We never `dangerouslySetInnerHTML` here; XSS via toast copy would
 *   require an attacker to control an i18n value, which they cannot.
 */

import type { JSX } from 'react';
import { useEffect } from 'react';
import { useToastStore, type Toast } from '../../stores/toast.store';
import { XIcon } from './Icons';

export function Toaster(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed top-4 inset-x-0 z-50 flex flex-col items-center gap-2 px-4"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastItem({ toast }: { toast: Toast }): JSX.Element {
  const dismiss = useToastStore((s) => s.dismiss);
  useEffect(() => {
    if (toast.durationMs <= 0) return;
    const id = window.setTimeout(() => dismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(id);
  }, [toast.id, toast.durationMs, dismiss]);

  const palette = KIND_STYLES[toast.kind];
  return (
    <div
      role="alert"
      className={`pointer-events-auto w-full max-w-md rounded-lg shadow-lift border border-slate-200/80 px-4 py-3 text-sm flex items-start gap-3 ${palette}`}
    >
      <span className="flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        className="rounded-md p-0.5 text-current opacity-60 hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        aria-label="dismiss"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

const KIND_STYLES: Record<Toast['kind'], string> = {
  success: 'bg-emerald-50 text-emerald-900',
  info: 'bg-blue-50 text-blue-900',
  error: 'bg-red-50 text-red-900',
};
